import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, CheckCircle2, Users, Trophy, Megaphone, Share2, Loader2, Upload, FileText, X,
} from "lucide-react";

const WHO = [
  { icon: Trophy, t: "High-performing students" },
  { icon: Users, t: "Student leaders & club officers" },
  { icon: Megaphone, t: "Study & productivity creators" },
  { icon: Share2, t: "TikTok / Instagram creators" },
];

const Field = ({ label, k, f, set, type = "text", ph = "", req = false }) => (
  <div>
    <label className="text-xs font-medium text-slate-500">{label}{req && " *"}</label>
    <Input type={type} value={f[k]} onChange={set(k)} placeholder={ph} data-testid={`amb-${k}`} className="mt-1" />
  </div>
);

export default function BecomeAmbassador() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resume, setResume] = useState(null);
  const [f, setF] = useState({
    full_name: "", email: "", university: "", major: "", academic_year: "", gpa: "",
    graduation_year: "", organizations: "", leadership: "", reach: "", involvement: "",
    tiktok: "", instagram: "", youtube: "", other_social: "", follower_counts: "",
    profile_links: "", why: "", how_promote: "", promoted_before: "", consent: false,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const uploadResume = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post("/ambassador/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResume({ id: res.data.file_id, name: res.data.filename });
      toast.success("File uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name || !f.email || !f.university) { toast.error("Please fill in your name, email, and university."); return; }
    if (!f.consent) { toast.error("Please agree to be contacted about the program."); return; }
    setLoading(true);
    try {
      await api.post("/ambassador/apply", { ...f, resume_file_id: resume?.id || null });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application.");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="application-success">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-slate-900">Application received!</h1>
          <p className="mt-2 text-slate-600">Thanks for applying to the Syllabify Campus Ambassador program. Your status is <span className="font-semibold">Pending</span> — we'll be in touch by email.</p>
          <Button onClick={() => navigate("/")} className="mt-6 bg-slate-900 hover:bg-slate-800 rounded-lg">Back to home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")}><Logo /></button>
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Home</button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 mb-4">Campus Ambassador Program</span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Become a Syllabify Campus Ambassador</h1>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Help fellow students get organized — and get rewarded for it. We're looking for connected, driven students to bring Syllabify to their campus.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {WHO.map((w) => (
            <div key={w.t} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-700"><w.icon className="h-4.5 w-4.5 h-5 w-5" /></span>
              <span className="text-sm font-medium text-slate-700">{w.t}</span>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Personal information</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field f={f} set={set} label="Full name" k="full_name" req ph="Jordan Lee" />
              <Field f={f} set={set} label="Email" k="email" type="email" req ph="you@school.edu" />
              <Field f={f} set={set} label="University / college" k="university" req ph="UCLA" />
              <Field f={f} set={set} label="Major" k="major" ph="Computer Science" />
              <Field f={f} set={set} label="Academic year" k="academic_year" ph="Junior" />
              <Field f={f} set={set} label="GPA or GPA range" k="gpa" ph="3.8" />
              <Field f={f} set={set} label="Graduation year" k="graduation_year" ph="2027" />
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Campus influence</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field f={f} set={set} label="Student organizations / clubs" k="organizations" ph="CS Club, Student Gov" />
              <Field f={f} set={set} label="Leadership positions" k="leadership" ph="Club President" />
              <Field f={f} set={set} label="Students you can reach (approx.)" k="reach" ph="2000" />
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-slate-500">Campus involvement</label>
                <Textarea value={f.involvement} onChange={set("involvement")} data-testid="amb-involvement" className="mt-1" placeholder="Tell us how you're involved on campus." />
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Social media</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field f={f} set={set} label="TikTok username" k="tiktok" ph="@handle" />
              <Field f={f} set={set} label="Instagram username" k="instagram" ph="@handle" />
              <Field f={f} set={set} label="YouTube" k="youtube" ph="channel" />
              <Field f={f} set={set} label="Other platform" k="other_social" ph="" />
              <Field f={f} set={set} label="Follower counts" k="follower_counts" ph="TikTok 12k, IG 5k" />
              <Field f={f} set={set} label="Links to profiles" k="profile_links" ph="https://…" />
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Your application</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500">Why do you want to become a Syllabify ambassador?</label>
                <Textarea value={f.why} onChange={set("why")} data-testid="amb-why" className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">How would you promote Syllabify on your campus?</label>
                <Textarea value={f.how_promote} onChange={set("how_promote")} data-testid="amb-how" className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Have you promoted a product or organization before?</label>
                <Textarea value={f.promoted_before} onChange={set("promoted_before")} data-testid="amb-promoted" className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Resume / media kit (optional)</label>
                {resume ? (
                  <div className="mt-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                    <FileText className="h-4 w-4 text-slate-400" /> <span className="flex-1 truncate">{resume.name}</span>
                    <button type="button" onClick={() => setResume(null)}><X className="h-4 w-4 text-slate-400" /></button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="amb-upload-resume" className="mt-1 rounded-lg">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" /> Upload file</>}
                  </Button>
                )}
                <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => uploadResume(e.target.files?.[0])} />
              </div>
            </div>
          </section>

          <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <Checkbox id="consent" checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} data-testid="amb-consent" className="mt-0.5" />
            <label htmlFor="consent" className="text-sm text-slate-600">I agree that Syllabify may contact me about the Campus Ambassador program.</label>
          </div>

          <Button type="submit" disabled={loading} data-testid="amb-submit" className="w-full bg-blue-600 hover:bg-blue-700 py-6 rounded-lg text-base font-medium">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit application"}
          </Button>
        </form>
      </div>
    </div>
  );
}
